import React, { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { exportScreenshots, screenshotRawEndpoint, carrierEndpoint } from "../utils/trustCallApi";
import { formatPhoneNumber } from "../utils/formatPhone";
import { getGalleryLogo } from "../utils/carrierDisplay";
import { getBrowserTimeZone, formatLocal } from "../utils/dateHelpers";
import type {
  TrustCallPhone,
  PhoneCallScreenshotData,
  ScreenshotItem,
  CallScreenShotRaw,
  HiyaReputationChecksResponse,
  HiyaCurrentReputation,
  HiyaParsedResponse,
  DailySnapshot,
  TrustCallAdvancedInsight,
} from "../types";
import VerizonAnalyticsTab from "./VerizonAnalyticsTab";
import AdvancedInsightsTab from "./AdvancedInsightsTab";
import TrustCallDebugTab from "./TrustCallDebugTab";
import WarmupDebugTab from "./WarmupDebugTab";

interface PhoneDetailsModalProps {
  show: boolean;
  phone: TrustCallPhone | null;
  isCanadian?: boolean;
  onClose: () => void;
}

type TabKey = "screenshots" | "att-analytics" | "score-history" | "verizon-analytics" | "insights" | "debug" | "warmup";

const SERVICE_TYPE_MAP: Record<number, string> = {
  1: "Scanning",
  2: "Remediation",
  3: "Remediation Daily",
  4: "One-Time Scan",
  5: "Branding",
};

const ADDED_FROM_MAP: Record<string, string> = {
  W: "Web",
  A: "API",
  B: "Bulk",
};

// Inline chip styles matching MVC renderScreenshots
const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
};

const labelStyle: React.CSSProperties = {
  fontWeight: 700,
  opacity: 0.7,
  marginRight: 2,
};

function convertTZ(date: Date, tzString: string): string {
  return date.toLocaleString("en-US", {
    timeZone: tzString,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Render a JSON value with syntax-highlighted colors (React elements, no raw HTML).
// Matches the MVC colorized JSON display: keys=purple, strings=green, numbers/booleans=blue, null=gray.
function JsonView({ data }: { data: unknown }): React.ReactElement {
  const render = (value: unknown, indent: number): React.ReactNode => {
    if (value === null) return <span style={{ color: "#888" }}>null</span>;
    if (typeof value === "boolean") return <span style={{ color: "#1a01cc" }}>{String(value)}</span>;
    if (typeof value === "number") return <span style={{ color: "#1a01cc" }}>{value}</span>;
    if (typeof value === "string") return <span style={{ color: "#0b7e34" }}>"{value}"</span>;

    const pad = "  ".repeat(indent);
    const padClose = "  ".repeat(Math.max(indent - 1, 0));

    if (Array.isArray(value)) {
      if (value.length === 0) return <>{"[]"}</>;
      return (
        <>{"[\n"}{value.map((item, i) => (
          <React.Fragment key={i}>{pad}{render(item, indent + 1)}{i < value.length - 1 ? "," : ""}{"\n"}</React.Fragment>
        ))}{padClose}{"]"}</>
      );
    }
    if (typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) return <>{"{}"}</>;
      return (
        <>{"{\n"}{entries.map(([key, val], i) => (
          <React.Fragment key={key}>{pad}<span style={{ color: "#881391" }}>"{key}"</span>: {render(val, indent + 1)}{i < entries.length - 1 ? "," : ""}{"\n"}</React.Fragment>
        ))}{padClose}{"}"}</>
      );
    }
    return <>{String(value)}</>;
  };

  return <>{render(data, 1)}</>;
}



// Metric color class — matches MVC metricColor()
function metricColorClass(val: string | null | undefined): string {
  if (val == null) return "";
  const lower = String(val).toLowerCase();
  if (lower === "good" || lower === "normal" || lower === "high") return "text-success";
  if (lower === "moderate" || lower === "medium" || lower === "neutral") return "text-warning";
  if (lower === "low" || lower === "poor" || lower === "bad" || lower === "very_poor") return "text-danger";
  return "";
}

// Capitalize first letter — matches MVC capFirst()
function capFirst(s: string | null | undefined): string {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Parse Hiya checks to extract reputation data from responseBody JSON
function parseHiyaChecks(data: HiyaReputationChecksResponse) {
  const checks = data.checks || [];

  // Find the most recent successful check with reputation data
  let latestReputation: (HiyaCurrentReputation & {
    _reputationAvailable: string | null;
  }) | null = null;
  let latestRequestedAt: string | null = null;

  for (const c of checks) {
    if (c.responseStatusCode >= 200 && c.responseStatusCode < 300 && c.responseBody) {
      try {
        const resp: HiyaParsedResponse = JSON.parse(c.responseBody);
        const phone = resp.phones?.[0];
        const rep = phone?.currentReputation;
        if (rep && (rep.maturityMetric != null || rep.desirabilityMetric != null ||
          rep.engagementMetric != null || rep.reactionMetric != null)) {
          latestReputation = {
            ...rep,
            _reputationAvailable: phone.reputationAvailable != null
              ? String(phone.reputationAvailable)
              : null,
          };
          latestRequestedAt = c.requestedAt;
          break;
        }
      } catch { /* skip malformed */ }
    }
  }

  // Build score history from all successful checks
  const history: {
    date: Date | null;
    flag: string;
    maturity: string;
    desirability: string;
    engagement: string;
    reaction: string;
  }[] = [];

  for (const c of checks) {
    if (c.responseStatusCode >= 200 && c.responseStatusCode < 300 && c.responseBody) {
      try {
        const r: HiyaParsedResponse = JSON.parse(c.responseBody);
        const rep = r.phones?.[0]?.currentReputation;
        if (rep) {
          history.push({
            date: c.requestedAt ? new Date(c.requestedAt) : null,
            flag: rep.flagStatus || "—",
            maturity: rep.maturityMetric || "—",
            desirability: rep.desirabilityMetric || "—",
            engagement: rep.engagementMetric || "—",
            reaction: rep.reactionMetric || "—",
          });
        }
      } catch { /* skip */ }
    }
  }

  return { latestReputation, latestRequestedAt, history };
}

// SVG icons for AT&T Analytics metric cards
const MetricIcons = {
  maturity: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="#5B9BD5">
      <path d="M12 1.673A10.327 10.327 0 1 0 22.327 12 10.34 10.34 0 0 0 12 1.673m0 19.44A9.113 9.113 0 1 1 21.113 12 9.12 9.12 0 0 1 12 21.113M18.277 12a.607.607 0 0 1-.607.607H12a.607.607 0 0 1-.607-.607V6.33a.608.608 0 0 1 1.215 0v5.063h5.063a.607.607 0 0 1 .607.607z" />
    </svg>
  ),
  desirability: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="#5B9BD5">
      <path d="M16.48 9.14a.607.607 0 0 1 0 .86l-5.67 5.67a.607.607 0 0 1-.86 0l-2.43-2.43a.607.607 0 0 1 .86-.86l2 2 5.24-5.24a.607.607 0 0 1 .86 0M22.327 12A10.327 10.327 0 1 1 12 1.673 10.34 10.34 0 0 1 22.327 12m-1.214 0A9.113 9.113 0 1 0 12 21.113 9.12 9.12 0 0 0 21.113 12" />
    </svg>
  ),
  engagement: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="#5B9BD5">
      <path d="M23.705 14.106a.607.607 0 0 1-.851-.121 5.42 5.42 0 0 0-4.374-2.187.607.607 0 0 1 0-1.214 2.633 2.633 0 1 0-2.55-3.29.607.607 0 0 1-1.176-.304 3.847 3.847 0 1 1 6.066 4.013 6.65 6.65 0 0 1 3.006 2.255.607.607 0 0 1-.121.85zm-5.509 6.5a.607.607 0 0 1-.222.831.6.6 0 0 1-.304.081.6.6 0 0 1-.527-.303 5.974 5.974 0 0 0-10.289 0 .607.607 0 1 1-1.051-.607 7.08 7.08 0 0 1 3.665-3.084 4.657 4.657 0 1 1 5.072 0 7.08 7.08 0 0 1 3.654 3.082zM12 17.063a3.443 3.443 0 1 0-3.443-3.443A3.444 3.444 0 0 0 12 17.063M6.127 11.19a.607.607 0 0 0-.607-.607 2.633 2.633 0 1 1 2.55-3.292.607.607 0 1 0 1.176-.303A3.847 3.847 0 1 0 3.18 11a6.65 6.65 0 0 0-3.006 2.256.607.607 0 1 0 .972.729 5.42 5.42 0 0 1 4.374-2.187.607.607 0 0 0 .607-.607z" />
    </svg>
  ),
  reaction: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="#5B9BD5">
      <path d="M20.91 4.103H3.09A1.416 1.416 0 0 0 1.673 5.52v16.2a1.4 1.4 0 0 0 .819 1.285 1.4 1.4 0 0 0 .599.133 1.4 1.4 0 0 0 .911-.334l.006-.005L7.367 19.9h13.544a1.416 1.416 0 0 0 1.417-1.418V5.522a1.416 1.416 0 0 0-1.417-1.417zm.203 14.377a.204.204 0 0 1-.203.203H7.14a.6.6 0 0 0-.397.147l-3.526 3.045a.203.203 0 0 1-.33-.155V5.52a.204.204 0 0 1 .203-.203h17.82a.204.204 0 0 1 .203.203zm-5.265-8.1a.607.607 0 0 1-.607.607h-6.48a.608.608 0 0 1 0-1.215h6.48a.607.607 0 0 1 .607.607zm0 3.24a.607.607 0 0 1-.607.607h-6.48a.608.608 0 0 1 0-1.215h6.48a.607.607 0 0 1 .607.607z" />
    </svg>
  ),
} as const;

// Metric card definitions — matches MVC metrics array
const ATT_METRICS = [
  { key: "maturityMetric" as const, label: "Maturity", desc: "How long the number has been active", icon: MetricIcons.maturity },
  { key: "desirabilityMetric" as const, label: "Desirability", desc: "How likely calls are wanted by recipients", icon: MetricIcons.desirability },
  { key: "engagementMetric" as const, label: "Engagement", desc: "How often recipients answer calls", icon: MetricIcons.engagement },
  { key: "reactionMetric" as const, label: "Reaction", desc: "How recipients respond after answering", icon: MetricIcons.reaction },
] as const;

// Spam flag cell — matches MVC: function flag(v) { return v ? '<span class="text-danger fw-bold">Spam</span>' : '<span class="text-success">Clean</span>'; }
function renderSpamFlag(isSpam: boolean): React.ReactNode {
  return isSpam
    ? <span className="text-danger fw-bold">Spam</span>
    : <span className="text-success">Clean</span>;
}

const PhoneDetailsModal: React.FC<PhoneDetailsModalProps> = ({
  show,
  phone,
  isCanadian = false,
  onClose,
}) => {
  const { isSysadmin } = useAppSelector(selectAuth);
  const [activeTab, setActiveTab] = useState<TabKey>("screenshots");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [isMaximized, setIsMaximized] = useState(false);
  // Track which screenshot debug/raw sections are expanded
  const [expandedDebug, setExpandedDebug] = useState<Set<number>>(new Set());
  const [expandedRaw, setExpandedRaw] = useState<Set<number>>(new Set());
  const [rawDataCache, setRawDataCache] = useState<Record<number, CallScreenShotRaw | null>>({});
  const [rawDataLoading, setRawDataLoading] = useState<Set<number>>(new Set());

  // Clean phone to digits only — MVC does Utility.CleanPhoneNo() before calling services;
  // WebAPI route params need clean numbers to match DB lookups.
  const phoneNumber = (phone?.phoneNumber ?? "").replace(/\D/g, "");
  const timeZone = getBrowserTimeZone();

  // Initial screenshots load (dateCall="") — populates date list + first screenshots.
  // MVC only populates the date dropdown once; we cache this query separately.
  const initialScreenshotsQuery = useQuery<PhoneCallScreenshotData>({
    queryKey: ["trustcall", "screenshots", phoneNumber, ""],
    queryFn: () => callApi(
      exportScreenshots(phoneNumber, "", timeZone),
      HttpMethod.GET
    ),
    enabled: show && !!phoneNumber && activeTab === "screenshots",
  });

  // Date-specific screenshots — only fires when a date other than the initial is selected.
  // MVC behavior: on date change, only the gallery + info panel update; the date dropdown stays.
  const dateScreenshotsQuery = useQuery<PhoneCallScreenshotData>({
    queryKey: ["trustcall", "screenshots", phoneNumber, selectedDate],
    queryFn: () => callApi(
      exportScreenshots(phoneNumber, selectedDate, timeZone),
      HttpMethod.GET
    ),
    enabled: show && !!phoneNumber && activeTab === "screenshots" && selectedDate !== ""
      && selectedDate !== (initialScreenshotsQuery.data?.dateCalls?.[0] ?? ""),
  });

  // Use date-specific data when a date is selected, otherwise use initial data.
  // dateCalls always comes from the initial load (MVC never repopulates the dropdown).
  const isInitialDate = selectedDate === "" || selectedDate === (initialScreenshotsQuery.data?.dateCalls?.[0] ?? "");
  const screenshotsData = isInitialDate
    ? initialScreenshotsQuery.data
    : dateScreenshotsQuery.data ?? initialScreenshotsQuery.data;
  const isScreenshotsLoading = !initialScreenshotsQuery.data && initialScreenshotsQuery.isLoading;
  const isGalleryRefetching = !isInitialDate && dateScreenshotsQuery.isFetching;

  // Advanced insights — shared by "Advanced Insights" and "Verizon Analytics" tabs
  const insightsQuery = useQuery({
    queryKey: ["trustcall", "insights", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "advanced-insights"), HttpMethod.GET),
    enabled: show && !!phoneNumber && (activeTab === "insights" || activeTab === "verizon-analytics"),
  });

  // Hiya / AT&T analytics
  const hiyaQuery = useQuery({
    queryKey: ["trustcall", "hiya", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "hiya"), HttpMethod.GET),
    enabled: show && !!phoneNumber && activeTab === "att-analytics",
  });

  // Debug info — shared by "Daily Score History" tab (dailySnapshots) and "TrustCall Debug" tab
  const debugQuery = useQuery({
    queryKey: ["trustcall", "debug", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "debug-info"), HttpMethod.GET),
    enabled: show && !!phoneNumber && (activeTab === "debug" || activeTab === "score-history") && !!isSysadmin,
  });

  // Warmup debug
  const warmupQuery = useQuery({
    queryKey: ["trustcall", "warmup", phoneNumber],
    queryFn: () => callApi(carrierEndpoint(phoneNumber, "warmup-debug"), HttpMethod.GET),
    enabled: show && !!phoneNumber && activeTab === "warmup" && !!isSysadmin,
  });

  // Default selectedDate to first entry once data loads, so "Most Recent" is highlighted
  useEffect(() => {
    const dates = initialScreenshotsQuery.data?.dateCalls;
    if (dates && dates.length > 0 && selectedDate === "") {
      setSelectedDate(dates[0]);
    }
  }, [initialScreenshotsQuery.data, selectedDate]);

  // Reset state when modal opens with a new phone
  const handleClose = useCallback(() => {
    setActiveTab("screenshots");
    setSelectedDate("");
    setIsMaximized(false);
    setExpandedDebug(new Set());
    setExpandedRaw(new Set());
    setRawDataCache({});
    setRawDataLoading(new Set());
    onClose();
  }, [onClose]);

  // Fetch raw screenshot data on demand (sysadmin only)
  const loadRawData = useCallback(async (screenshotId: number) => {
    if (rawDataCache[screenshotId] !== undefined) return;
    setRawDataLoading((prev) => new Set(prev).add(screenshotId));
    try {
      const data = await callApi(
        screenshotRawEndpoint(phoneNumber, screenshotId),
        HttpMethod.GET
      );
      setRawDataCache((prev) => ({ ...prev, [screenshotId]: data }));
    } catch {
      setRawDataCache((prev) => ({ ...prev, [screenshotId]: null }));
    } finally {
      setRawDataLoading((prev) => {
        const next = new Set(prev);
        next.delete(screenshotId);
        return next;
      });
    }
  }, [phoneNumber, rawDataCache]);

  const toggleDebug = (id: number) => {
    setExpandedDebug((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleRaw = (id: number) => {
    setExpandedRaw((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        loadRawData(id);
      }
      return next;
    });
  };

  if (!show || !phone) return null;

  const phone10 = (phone.phoneNumber || "").replace(/\D/g, "").slice(-10);

  // Tab icon style — 18px matching MVC Kendo icon font-size, inherits nav-link color via currentColor
  const iconStyle: React.CSSProperties = { flexShrink: 0, marginBottom: 2 };

  const tabs: { key: TabKey; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { key: "screenshots", label: "Screenshots", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="M448 128h-64l-64-64H192l-64 64H64c-17.6 0-32 14.4-32 32v288c0 17.6 14.4 32 32 32h384c17.6 0 32-14.4 32-32V160c0-17.6-14.4-32-32-32M256 416c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112m64-112c0 35.3-28.7 64-64 64s-64-28.7-64-64 28.7-64 64-64 64 28.7 64 64"/></svg> },
    { key: "att-analytics", label: isCanadian ? "Rogers/Bell Analytics" : "AT&T Analytics", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="M480 448v32H0V32h32v416zm0-96c.1 17.7-14.2 32-31.9 32.1s-32-14.2-32.1-31.9c0-1.3.1-2.7.2-4l-83.7-62.8c-8 3.4-17.1 3.4-25.2 0l-83.7 62.8c2.1 17.5-10.4 33.5-27.9 35.6s-33.5-10.4-35.6-27.9c-.7-6.2.3-12.4 3.1-18l-49-73.6-22.7 39.6c8.9 15.3 3.7 34.9-11.5 43.8s-34.9 3.7-43.8-11.5-3.7-34.9 11.5-43.8c4.9-2.8 10.4-4.3 16-4.4l30.6-53.5-28.4-42.6c-.7 0-1.4.1-2.1.1-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32c0 4.9-1.1 9.8-3.3 14.2l19.5 29.3 52.2-91.3c-8.9-15.3-3.8-34.9 11.5-43.8s34.9-3.8 43.8 11.5c2.5 4.4 4 9.2 4.3 14.3l78.4 39.2c14.7-9.7 34.6-5.7 44.3 9 .3.5.7 1.1 1 1.6h72.6c8.8-15.3 28.4-20.5 43.7-11.7s20.5 28.4 11.7 43.7-28.4 20.5-43.7 11.7c-4.9-2.8-8.9-6.8-11.7-11.7h-72.6c-8.8 15.3-28.4 20.5-43.7 11.7-9.3-5.4-15.3-15.1-15.9-25.8l-78.4-39.2c-5.2 3.4-11.3 5.3-17.5 5.3l-60 105.2 57.9 86.9c.7 0 1.4-.1 2.1-.1 4.3 0 8.6.9 12.6 2.6l83.7-62.8c-2.2-17.5 10.3-33.5 27.8-35.7s33.5 10.3 35.7 27.8q.45 3.9 0 7.8l83.7 62.8c16.2-7 35.1.6 42 16.8 1.6 4.1 2.5 8.4 2.5 12.7m-48-192c0 8.8 7.2 16 16 16s16-7.2 16-16-7.2-16-16-16-16 7.2-16 16m-128 0c0 8.8 7.2 16 16 16s16-7.2 16-16-7.2-16-16-16-16 7.2-16 16M176 96c0 8.8 7.2 16 16 16s16-7.2 16-16-7.2-16-16-16-16 7.2-16 16m-96 64c0-8.8-7.2-16-16-16s-16 7.2-16 16 7.2 16 16 16 16-7.2 16-16m0 160c0-8.8-7.2-16-16-16s-16 7.2-16 16 7.2 16 16 16 16-7.2 16-16m128 32c0-8.8-7.2-16-16-16s-16 7.2-16 16 7.2 16 16 16 16-7.2 16-16m128-96c0-8.8-7.2-16-16-16s-16 7.2-16 16 7.2 16 16 16 16-7.2 16-16m128 96c0-8.8-7.2-16-16-16s-16 7.2-16 16 7.2 16 16 16 16-7.2 16-16"/></svg> },
    { key: "score-history", label: "Daily Score History", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="M416 416H288V288h128zm64-352v384c0 17.6-14.4 32-32 32H64c-17.6 0-32-14.4-32-32V64c0-17.6 14.4-32 32-32h64V0h64v32h128V0h64v32h64c17.6 0 32 14.4 32 32m-32 128H64v255.9l.1.1 383.9-.1zm0-127.9q-.15-.15 0 0l-64-.1v32h-64V64H192v32h-64V64H64.1l-.1.1V160h384z"/></svg>, adminOnly: true },
    { key: "verizon-analytics", label: "Verizon Analytics", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="m320 165.3-128-64-128 128V32H32v448h448V32zm128 7.5L320 352 192 224 64 377.6v-103l134.3-134.3 126.1 63 123.6-103z"/></svg>, adminOnly: true },
    { key: "insights", label: "Advanced Insights", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="m472 187.7-51.2 11.9c-9.4-8-22.5-9.9-33.8-4.8L288.1 96c0-11.5-6.2-22.1-16.1-27.8V32h-32v36.4c-7.1 4.1-12.3 10.8-14.6 18.6L96.1 184c-8.6 0-16.7 3.4-22.8 9.5l-32.8-8-7.5 31.1 32.3 7.8c2.7 9.9 10 17.8 19.6 21.5l61 111.8c-3 8.9-2 18.7 2.9 26.7l-56.1 77.3 25.9 18.8L177 400c8.5-.2 16.5-3.8 22.3-10l122 18.8c3.9 13.8 16.5 23.2 30.8 23.2 1.9 0 3.9-.2 5.8-.5l35.6 49 25.9-18.8-37.1-51.1c3.7-10.4 1.7-22-5.2-30.6l31.4-125.2c11.8-3.2 20.8-12.9 23.1-24.9l47.7-11.1zm-111.4 25.9L272 234.2V125.1zM256 80c8.8 0 16 7.2 16 16s-7.2 16-16 16-16-7.2-16-16 7.2-16 16-16m-21.1 39.9c1.6 1.4 3.3 2.6 5.1 3.7V234l-113.3-27.6c-.4-1.4-1-2.8-1.6-4.1zM96 232c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16m80 152c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16m7.2-47.2c-2.4-.5-4.8-.8-7.2-.8-1.8 0-3.7.2-5.5.5l-52.9-96.9c.7-.6 1.3-1.2 1.9-1.9l115.3 28zm23.6 22 49.1-67.6 61.5 84.6zM352 416c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16m-3.9-52.3L277 265.9l97.4-22.6c.9 1.2 1.9 2.4 3 3.5zM400 240c-8.8 0-16-7.2-16-16s7.2-16 16-16 16 7.2 16 16-7.2 16-16 16"/></svg>, adminOnly: true },
    { key: "debug", label: "TrustCall Debug", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="m462.7 317.9-41.5-31.4c1.8-9.9 2.8-20.1 2.8-30.5s-1-20.6-2.8-30.5l41.5-31.4c6.4-4.9 8.2-13.8 4.2-20.8L433 114.6c-4-7-12.6-9.9-20.1-6.7l-48 20.2c-15.4-13.2-33.3-23.6-52.8-30.5L305.8 46c-1-8-7.8-14-15.9-14h-67.8c-8.1 0-14.9 6-15.9 14l-6.5 51.6c-19.5 6.9-37.4 17.3-52.8 30.5l-48-20.2c-7.4-3.1-16-.2-20.1 6.7l-33.9 58.7c-4 7-2.2 15.9 4.2 20.8l41.5 31.4C89 235.4 88 245.6 88 256s1 20.6 2.8 30.5l-41.5 31.4c-6.4 4.9-8.2 13.8-4.2 20.8L79 397.4c4 7 12.6 9.9 20.1 6.7l48-20.2c15.4 13.2 33.3 23.6 52.8 30.5l6.5 51.6c1 8 7.8 14 15.9 14h67.8c8.1 0 14.9-6 15.9-14l6.5-51.6c19.5-6.9 37.4-17.3 52.8-30.5l48 20.2c7.4 3.1 16 .2 20.1-6.7l33.9-58.7c3.7-7 1.9-15.9-4.6-20.8M256 340c-46.4 0-84-37.6-84-84s37.6-84 84-84 84 37.6 84 84-37.6 84-84 84"/></svg>, adminOnly: true },
    { key: "warmup", label: "Warmup Debug", icon: <svg aria-hidden="true" style={iconStyle} width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="m331.9 364.2-29.6-22.4c1.3-7.1 2-14.3 2-21.8 0-7.4-.7-14.7-2-21.8l29.6-22.4c4.6-3.5 5.9-9.8 3-14.8l-24.2-42c-2.9-5-9-7.1-14.3-4.8L262 228.7c-11-9.4-23.8-16.9-37.7-21.8l-4.6-36.9c-.7-5.7-5.6-10-11.3-10H160c-5.8 0-10.6 4.3-11.3 10l-4.6 36.9c-13.9 4.9-26.7 12.4-37.7 21.8l-34.3-14.5c-5.3-2.2-11.5-.2-14.3 4.8l-24.3 42c-2.9 5-1.6 11.3 3 14.8l29.6 22.4c-1.3 7.1-2 14.3-2 21.8 0 7.4.7 14.7 2 21.8l-29.6 22.4c-4.6 3.5-5.9 9.8-3 14.8l24.2 42c2.9 5 9 7.1 14.3 4.8l34.3-14.5c11 9.4 23.8 16.9 37.7 21.8l4.6 36.9c.7 5.7 5.6 10 11.3 10h48.4c5.8 0 10.6-4.3 11.3-10l4.6-36.9c13.9-4.9 26.7-12.4 37.7-21.8l34.3 14.5c5.3 2.2 11.5.2 14.3-4.8l24.2-41.9c3-5 1.8-11.4-2.8-14.9M184.2 380c-33.1 0-60-26.9-60-60s26.9-60 60-60 60 26.9 60 60-26.9 60-60 60m293.1-225.5L459.4 141c.8-4.2 1.2-8.6 1.2-13 0-4.5-.4-8.8-1.2-13l17.8-13.4c2.7-2.1 3.5-5.9 1.8-8.9l-14.5-25.2c-1.8-3-5.4-4.2-8.6-2.8l-20.6 8.7c-6.7-5.7-14.3-10.1-22.7-13.1L409.9 38c-.4-3.4-3.3-6-6.8-6h-29c-3.4 0-6.4 2.5-6.8 6l-2.7 22.2c-8.3 2.9-16 7.5-22.7 13.1l-20.6-8.7c-3.2-1.4-6.9-.1-8.6 2.8l-14.5 25.2c-1.8 3-1 6.9 1.8 8.9l17.8 13.4c-.8 4.2-1.2 8.6-1.2 13 0 4.5.4 8.8 1.2 13L300 154.5c-2.7 2.1-3.5 5.9-1.8 8.9l14.5 25.2c1.8 3 5.4 4.2 8.6 2.8l20.6-8.7c6.7 5.7 14.3 10.1 22.7 13.1l2.7 22.2c.4 3.4 3.3 6 6.8 6h29c3.4 0 6.4-2.5 6.8-6l2.7-22.2c8.3-2.9 16-7.5 22.7-13.1l20.6 8.7c3.2 1.4 6.9.1 8.6-2.8l14.5-25.2c1.8-3 1-6.9-1.7-8.9m-88.7 9.5c-19.9 0-36.1-16.2-36.1-36.1s16.2-36.1 36.1-36.1 36.1 16.2 36.1 36.1c-.1 19.9-16.1 36.1-36.1 36.1"/></svg>, adminOnly: true },
  ];

  const visibleTabs = tabs.filter((t) => !t.adminOnly || isSysadmin);

  const renderLoading = () => (
    <div className="text-center py-4">
      <div className="spinner-border spinner-border-sm" role="status" />
      <span className="ms-2">Loading...</span>
    </div>
  );

  const renderJson = (data: unknown) => (
    <pre className="bg-light p-3 rounded small" style={{ maxHeight: 400, overflow: "auto" }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );

  // === Screenshots Tab ===
  const renderScreenshotsTab = () => {
    // Full loading spinner only on very first load (no data yet)
    if (isScreenshotsLoading) return renderLoading();

    const data = screenshotsData;
    if (!data) return <p className="text-muted">No screenshot data available.</p>;

    const screenshots: ScreenshotItem[] = data.screenShots?.url ?? [];
    // Date list always from initial query — MVC never repopulates the dropdown
    const dateCalls = initialScreenshotsQuery.data?.dateCalls ?? [];

    return (
      <div>
        {/* Row 1: Date selector + Info panel — matches MVC: <div class="row align-items-center"> */}
        <div className="row align-items-center">
          {/* Date selector listbox — MVC: <select size="5" class="form-control" style="cursor:pointer;max-height:80px"> */}
          <div className="col-md-3">
            <label>Select Date:</label>
            <select
              className="form-control"
              size={5}
              style={{ cursor: "pointer", maxHeight: 80, fontSize: "0.85rem" }}
              value={selectedDate || dateCalls[0]}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              {dateCalls.map((d, i) => (
                <option key={d} value={d}>
                  {i === 0 ? "Most Recent" : d}
                </option>
              ))}
            </select>
          </div>

          {/* Info panel — MVC: <div class="col-md-9" id="trustcall-info-panel"> */}
          <div className="col-md-9">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {/* Row 1: Monitored-in warning + Open in TNS */}
              {isSysadmin && data.otherActiveAccountCount > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span style={{
                    ...chipStyle,
                    background: "#fff3cd",
                    border: "1px solid #ffc107",
                    color: "#664d03",
                  }}>
                    <span className="k-icon k-i-warning" style={{ fontSize: 14 }} />
                    {" Monitored in "}
                    <strong>
                      {data.otherActiveAcctIds && data.otherActiveAcctIds.length > 0
                        ? data.otherActiveAcctIds.join(", ")
                        : `${data.otherActiveAccountCount} account(s)`}
                    </strong>
                  </span>
                  {phone10.length === 10 && (
                    <a
                      className="btn btn-sm btn-outline-primary"
                      href={`https://rsp.tnsi.com/insights?tn=1${phone10}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 20 }}
                    >
                      <span className="k-icon k-i-hyperlink-open-sm" style={{ fontSize: 14, marginRight: 4 }} />
                      Open in TNS (admin)
                    </a>
                  )}
                </div>
              )}
              {isSysadmin && data.otherActiveAccountCount === 0 && phone10.length === 10 && (
                <div>
                  <a
                    className="btn btn-sm btn-outline-primary"
                    href={`https://rsp.tnsi.com/insights?tn=1${phone10}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 20 }}
                  >
                    <span className="k-icon k-i-hyperlink-open-sm" style={{ fontSize: 14, marginRight: 4 }} />
                    Open in TNS (admin)
                  </a>
                </div>
              )}

              {/* Row 2: Label + Service Type chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {data.label && (
                  <span style={{
                    ...chipStyle,
                    background: "#e8f4fd",
                    border: "1px solid #b6d4fe",
                    color: "#084298",
                  }}>
                    <span style={labelStyle}>Label:</span> {data.label}
                  </span>
                )}
                {data.serviceType != null && (
                  <span style={{
                    ...chipStyle,
                    background: "#e0f5e9",
                    border: "1px solid #a3cfbb",
                    color: "#0a5c36",
                  }}>
                    <span style={labelStyle}>Service:</span>{" "}
                    {SERVICE_TYPE_MAP[data.serviceType] || `Type ${data.serviceType}`}
                  </span>
                )}
              </div>

              {/* Row 3: Added date chip */}
              {data.dateAdded && (
                <div>
                  <span style={{
                    ...chipStyle,
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    color: "#555",
                  }}>
                    <span style={labelStyle}>Added:</span>{" "}
                    {(() => {
                      const da = new Date(data.dateAdded!);
                      const dateStr = da.toLocaleDateString();
                      const timeStr = da.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                      let byStr = data.addedByUserName ? ` by ${data.addedByUserName}` : "";
                      if (data.addedFrom) {
                        byStr += ` via ${ADDED_FROM_MAP[data.addedFrom] || data.addedFrom}`;
                      }
                      return `${dateStr} ${timeStr}${byStr}`;
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gallery wrap — MVC CSS: #gallery-wrap { margin-top:10px; width:100%; position:relative; z-index:3; } */}
        <div className="row" style={{ marginTop: 10 }}>
          <div className="col-md-12" style={{ width: "100%", position: "relative", zIndex: 3 }}>
            {/* Loading overlay on gallery during date change — matches MVC $.loader behavior */}
            {isGalleryRefetching && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 4,
                background: "rgba(255,255,255,0.7)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            )}
            {screenshots.length > 0 ? (
              /* MVC CSS: #gallery { position:relative; left:0; top:0; list-style-type:none; padding:0; } */
              <ul style={{ position: "relative", left: 0, top: 0, listStyleType: "none", padding: 0 }}>
                {screenshots.map((ss) => {
                  const callDate = new Date(ss.dateTimeCall);
                  const logo = getGalleryLogo(ss.carrier, isCanadian);

                  return (
                    /* MVC CSS: #gallery li { height:300px; display:inline-table; margin-right:10px; } */
                    <li
                      key={ss.screenshotId}
                      style={{ height: 300, display: "inline-table", marginRight: 10 }}
                    >
                      {/* MVC: <div/>.append(logo) — CSS: #gallery li div { width:190px; } */}
                      <div style={{ width: 190 }}>
                        {logo && (
                          /* MVC CSS: #gallery li img.logo { margin-top:10px; height:30px; margin-bottom:10px; } */
                          <img
                            src={logo}
                            alt={ss.carrier}
                            style={{ marginTop: 10, height: 30, marginBottom: 10 }}
                          />
                        )}
                      </div>

                      {/* MVC CSS: #gallery li img.imgURL { width:190px; } */}
                      <img
                        src={ss.url}
                        alt={`${ss.carrier} screenshot`}
                        style={{ width: 190 }}
                      />

                      {/* MVC: <div/>.append(span).append(...) — CSS: #gallery li div { width:190px; } */}
                      <div style={{ width: 190 }}>
                        {/* MVC CSS: #gallery li div span.timeChecked { font-size:10px; font-weight:bold; } */}
                        <span style={{ fontSize: 10, fontWeight: "bold" }}>
                          Time: {convertTZ(callDate, timeZone)}
                        </span>

                        {/* Override badge (sysadmin only) */}
                        {isSysadmin && ss.nameOfDevice?.startsWith("Override-") && (
                          <span
                            className="badge bg-info text-dark d-block mt-1"
                            style={{ fontSize: 11 }}
                          >
                            Override Applied
                          </span>
                        )}

                        {/* Debug info + raw data toggles (sysadmin only) — MVC: cfg.showDebugInfo */}
                        {isSysadmin && (
                          <>
                            <a
                              href="#"
                              className="screenshot-debug-toggle d-block mt-1"
                              style={{ fontSize: 11 }}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleDebug(ss.screenshotId);
                              }}
                            >
                              Screenshot debug info [admin]
                            </a>
                            {expandedDebug.has(ss.screenshotId) && (
                              <div className="screenshot-debug-info" style={{ fontSize: 11, lineHeight: 1.3 }}>
                                Device: {ss.phoneNumberOfDevice || "Unknown"} |{" "}
                                {ss.deviceLocation || "Unknown"} |{" "}
                                {ss.stringVirtualOrHandset || "Unknown"}
                              </div>
                            )}

                            {/* Raw CallScreenShot data link */}
                            {ss.screenshotId && (
                              <>
                                <a
                                  href="#"
                                  className="d-block mt-1"
                                  style={{ fontSize: 11 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleRaw(ss.screenshotId);
                                  }}
                                >
                                  View raw data [admin]
                                </a>
                                {expandedRaw.has(ss.screenshotId) && (
                                  <div>
                                    {rawDataLoading.has(ss.screenshotId) ? (
                                      <div className="text-center">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                      </div>
                                    ) : rawDataCache[ss.screenshotId] ? (
                                      <pre
                                        className="bg-light p-2 mt-1"
                                        style={{
                                          fontSize: 10,
                                          maxHeight: 300,
                                          overflow: "auto",
                                          whiteSpace: "pre-wrap",
                                        }}
                                      >
                                        <JsonView data={rawDataCache[ss.screenshotId]} />
                                      </pre>
                                    ) : (
                                      <span className="text-danger" style={{ fontSize: 11 }}>
                                        Failed to load
                                      </span>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-muted">No screenshots available for this phone number.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // === AT&T Analytics Tab ===
  const renderAttAnalyticsTab = () => {
    const carrierLabel = isCanadian ? "Rogers/Bell" : "AT&T";

    if (hiyaQuery.isLoading) return renderLoading();
    if (hiyaQuery.isError) {
      return (
        <div className="alert alert-danger mb-0">
          Failed to load {carrierLabel} analytics.
        </div>
      );
    }
    if (!hiyaQuery.data) {
      return (
        <div className="alert alert-info mb-0">
          No {carrierLabel} analytics data available for this number.
        </div>
      );
    }

    const { latestReputation, latestRequestedAt, history } = parseHiyaChecks(
      hiyaQuery.data as HiyaReputationChecksResponse
    );

    if (!latestReputation) {
      return (
        <div className="alert alert-info mb-0">
          No {carrierLabel} analytics data available for this number.
        </div>
      );
    }

    const retrievedAt = latestRequestedAt
      ? formatLocal(new Date(latestRequestedAt))
      : "—";
    const flagStatus = latestReputation.flagStatus || "—";
    const flagClass = flagStatus === "unflagged" ? "text-success" : "text-danger";

    return (
      <div style={{ paddingTop: 10 }}>
        {/* Row 1: Flag Status + Last Retrieved + Reputation Available */}
        <div className="row mb-4 mx-1">
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">Flag Status</h5>
                <h2 className={flagClass}>
                  {capFirst(flagStatus)}
                </h2>
                {latestReputation.flagCategory && (
                  <small className="text-muted">
                    Category: {latestReputation.flagCategory}
                  </small>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">Last Retrieved</h5>
                <p className="mb-0">{retrievedAt}</p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">Reputation Available</h5>
                <p className="mb-0">
                  {latestReputation._reputationAvailable || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Four metric cards */}
        <div className="row mx-1">
          {ATT_METRICS.map((m) => {
            const val = latestReputation[m.key];
            const displayVal = capFirst(val);
            return (
              <div className="col-md-3 mb-3" key={m.key}>
                <div className="card h-100">
                  <div className="card-body text-center">
                    {m.icon}
                    <h6 className="card-title mt-2">{m.label}</h6>
                    <h3 className={`mb-1 ${metricColorClass(val)}`}>
                      {displayVal}
                    </h3>
                    <small className="text-muted">{m.desc}</small>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Score History table */}
        <div className="mx-1 mt-3">
          <h5>Score History</h5>
          {history.length === 0 ? (
            <div className="text-muted">No score history available.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Flag Status</th>
                    <th>Maturity</th>
                    <th>Desirability</th>
                    <th>Engagement</th>
                    <th>Reaction</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => {
                    const dt = formatLocal(h.date);
                    const rowFlagClass =
                      h.flag === "unflagged"
                        ? "text-success"
                        : h.flag === "—"
                          ? ""
                          : "text-danger";
                    return (
                      <tr key={idx}>
                        <td>{dt}</td>
                        <td className={rowFlagClass}>{capFirst(h.flag)}</td>
                        <td className={metricColorClass(h.maturity)}>{capFirst(h.maturity)}</td>
                        <td className={metricColorClass(h.desirability)}>{capFirst(h.desirability)}</td>
                        <td className={metricColorClass(h.engagement)}>{capFirst(h.engagement)}</td>
                        <td className={metricColorClass(h.reaction)}>{capFirst(h.reaction)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // === Daily Score History Tab ===
  const renderScoreHistoryTab = () => {
    const attLabel = isCanadian ? "Rogers/Bell" : "AT&T";

    if (debugQuery.isLoading) return renderLoading();
    if (debugQuery.isError) {
      return <div className="alert alert-danger mb-0">Failed to load score history.</div>;
    }

    const data = debugQuery.data as { dailySnapshots?: DailySnapshot[] } | undefined;
    const snapshots = data?.dailySnapshots;

    if (!snapshots || snapshots.length === 0) {
      return <div className="alert alert-info">No daily snapshots found.</div>;
    }

    return (
      <div style={{ paddingTop: 10 }}>
        <h5>Daily Score History (Last 30 Days)</h5>
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>Date</th>
                <th>Verizon</th>
                <th>{attLabel}</th>
                <th>T-Mobile</th>
                <th>RoboKiller</th>
                <th>Nomorobo</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s, idx) => {
                const dt = s.dateFor
                  ? formatLocal(new Date(s.dateFor)).split(",")[0]
                  : "—";
                return (
                  <tr key={idx}>
                    <td>{dt}</td>
                    <td>{renderSpamFlag(s.isVerizonSpam)}</td>
                    <td>{renderSpamFlag(s.isATTSpam)}</td>
                    <td>{renderSpamFlag(s.isTMobilSpam)}</td>
                    <td>{renderSpamFlag(s.isFlaggedRK)}</td>
                    <td>{renderSpamFlag(s.isFlaggedNomorobo)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "screenshots":
        return renderScreenshotsTab();
      case "att-analytics":
        return renderAttAnalyticsTab();
      case "score-history":
        return renderScoreHistoryTab();
      case "verizon-analytics": {
        if (insightsQuery.isLoading) return renderLoading();
        if (insightsQuery.isError) return <div className="alert alert-danger mb-0">Failed to load Verizon analytics.</div>;
        const rows = insightsQuery.data as TrustCallAdvancedInsight[] | undefined;
        if (!rows || !Array.isArray(rows) || rows.length === 0)
          return <div className="alert alert-info mb-0">No Verizon analytics data available for this number.</div>;
        return <VerizonAnalyticsTab data={rows[0]} />;
      }
      case "insights": {
        if (insightsQuery.isLoading) return renderLoading();
        if (insightsQuery.isError) return <div className="alert alert-danger mb-0">Failed to load advanced insights.</div>;
        const insightRows = insightsQuery.data as TrustCallAdvancedInsight[] | undefined;
        if (!insightRows || !Array.isArray(insightRows) || insightRows.length === 0)
          return <div className="alert alert-info mb-0">No insights found for this account.</div>;
        return <AdvancedInsightsTab phoneNumber={phoneNumber} data={insightRows[0]} />;
      }
      case "debug":
        if (debugQuery.isLoading) return renderLoading();
        if (!debugQuery.data) return <p className="text-muted">No debug info available.</p>;
        return <TrustCallDebugTab data={debugQuery.data} />;
      case "warmup":
        if (warmupQuery.isLoading) return renderLoading();
        if (!warmupQuery.data) return <p className="text-muted">No warmup debug info available.</p>;
        return <WarmupDebugTab data={warmupQuery.data} />;
      default:
        return null;
    }
  };

  // Modal dialog — matches MVC: resize:both, overflow:auto, min/max widths
  const dialogStyle: React.CSSProperties = isMaximized
    ? { width: "100vw", maxWidth: "100vw", height: "100vh", margin: 0, resize: "none" }
    : { maxWidth: 1250, minWidth: 500, minHeight: 300, resize: "both", overflow: "auto" };
  const contentStyle: React.CSSProperties = isMaximized
    ? { height: "100vh", borderRadius: 0 }
    : {};

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        style={dialogStyle}
      >
        <div className="modal-content" style={contentStyle}>
          {/* Header with maximize + close buttons matching MVC */}
          <div className="modal-header" style={{ justifyContent: "flex-start", gap: 8 }}>
            <h3 className="modal-title" style={{ marginRight: "auto" }}>
              Details for {formatPhoneNumber(phone.phoneNumber)}
            </h3>
            {/* Maximize / Restore button — inline after title, matching MVC Kendo Window */}
            <button
                type="button"
                title={isMaximized ? "Restore" : "Maximize"}
                style={{
                  width: 32, height: 32,
                  border: "1px solid #ccc", borderRadius: 4,
                  background: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#e9ecef")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
                onClick={() => setIsMaximized((prev) => !prev)}
              >
                {isMaximized ? (
                  // Restore icon
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3.5" y="1.5" width="9" height="9" rx="1" />
                    <path d="M10.5 4.5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v6.5a1 1 0 001 1h1.5" />
                  </svg>
                ) : (
                  // Maximize icon
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="1.5" y="1.5" width="11" height="11" rx="1" />
                  </svg>
                )}
              </button>
              {/* Close button */}
              <button
                type="button"
                title="Close"
                style={{
                  width: 32, height: 32,
                  border: "1px solid #ccc", borderRadius: 4,
                  background: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#e9ecef")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
                onClick={handleClose}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="2" y1="2" x2="12" y2="12" />
                  <line x1="12" y1="2" x2="2" y2="12" />
                </svg>
              </button>
          </div>

          {/* Scoped styles matching MVC _PhoneDetailDialog.cshtml */}
          <style>{`
            .phone-details-modal .table { font-size: 14px; border: none; }
            .phone-details-modal .table td,
            .phone-details-modal .table th { padding: 2px 6px; border: none; }
            .phone-details-modal .debug-json-pre { white-space: pre-wrap; word-break: break-all; max-height: 300px; overflow: auto; font-size: 11px; background: #f8f9fa; margin: 0; padding: 8px; }
            #screenshotsDropdown,
            #screenshotsDropdown:focus {
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
              background: #fff !important;
            }
            #screenshotsDropdown option {
              padding: 2px 8px;
              border: none !important;
              border-radius: 0 !important;
              background: #fff !important;
              color: #000 !important;
            }
            #screenshotsDropdown option:checked {
              background: #c0c0c0 linear-gradient(0deg, #c0c0c0 0%, #c0c0c0 100%) !important;
              color: #000 !important;
            }
          `}</style>
          <div className="modal-body phone-details-modal">
            {/* Tab navigation — matches MVC _PhoneDetailDialog.cshtml CSS + structure exactly:
                .nav-tabs { flex-wrap: nowrap; align-items: stretch; }
                .nav-tabs .nav-link { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; }
                .nav-tabs .nav-link:hover { border-color: #dee2e6 #dee2e6 #dee2e6; } */}
            {/* Tab navigation — matches MVC _PhoneDetailDialog.cshtml layout.
                Active tab has colored bottom border matching tc-card-header (#475666).
                Icons inherit text color (dark gray), not blue. */}
            <ul className="nav nav-tabs" style={{ flexWrap: "nowrap", alignItems: "stretch", borderBottom: "2px solid #dee2e6" }}>
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <li className="nav-item" key={tab.key}>
                    <button
                      className={`nav-link tc-detail-tab${isActive ? " active" : ""}`}
                      onClick={() => setActiveTab(tab.key)}
                      type="button"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        padding: "10px 16px 6px",
                        border: "none",
                        borderRadius: 0,
                        marginBottom: "-2px",
                      }}
                    >
                      {tab.icon}
                      <span style={{ fontSize: 13, marginTop: 2 }}>{tab.label}</span>
                      {tab.adminOnly && (
                        <span
                          className="badge bg-secondary"
                          style={{ fontSize: 9, marginTop: 2 }}
                        >
                          ADMIN
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Tab content */}
            <div style={{ marginTop: 12 }}>
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneDetailsModal;
